import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import { i18n } from "../i18n";

/** A2K 风格的技能多选器，供 Codex 和定时任务共用。 */
export interface A2kSkillOption {
  name: string;
  description?: string;
  description_zh?: string;
  enabled?: boolean;
}

@customElement("a2k-skill-multiselect")
export class A2kSkillMultiselect extends LitElement {
  @property({ attribute: false }) skills: A2kSkillOption[] = [];
  @property({ attribute: false }) selected: string[] = [];
  @property({ type: Boolean }) disabled = false;
  @state() private filter = "";

  static styles = css`
    :host {
      display: block;
      min-width: 0;
      color: #000;
      font: inherit;
    }
    *,
    *::before,
    *::after {
      animation: none !important;
      transition: none !important;
      scroll-behavior: auto !important;
    }
    fieldset {
      min-width: 0;
      margin: 0;
      padding: 10px;
      background: #d4d0c8;
      border: 2px groove #fff;
    }
    legend {
      padding: 0 4px;
      font-weight: bold;
    }
    .filter {
      margin-bottom: 6px;
    }
    .filter input {
      box-sizing: border-box;
      width: 100%;
      min-height: 28px;
      padding: 2px 5px;
      border: none;
      border-radius: 0;
      box-shadow:
        inset -1px -1px #fff,
        inset 1px 1px #808080,
        inset -2px -2px #d4d0c8,
        inset 2px 2px #404040;
      background: #fff;
      color: #000;
      font: inherit;
    }
    .toolbar {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 8px;
    }
    button {
      min-width: 76px;
      min-min-height: 28px;
      padding: 3px 10px;
      border: none;
      border-radius: 0;
      background: #d4d0c8;
      color: #000;
      font: inherit;
      box-shadow:
        inset -1px -1px #404040,
        inset 1px 1px #fff,
        inset -2px -2px #808080,
        inset 2px 2px #dfdfdf;
    }
    button:active:not(:disabled) {
      box-shadow:
        inset 1px 1px #404040,
        inset -1px -1px #fff,
        inset 2px 2px #808080;
    }
    button:focus-visible {
      outline: 1px dotted #000;
      outline-offset: -5px;
    }
    button:disabled {
      color: #808080;
      text-shadow: 1px 1px #fff;
    }
    .list {
      max-height: 224px;
      overflow-y: scroll;
      overflow-x: hidden;
      padding: 2px;
      background: #fff;
      box-shadow:
        inset -1px -1px #fff,
        inset 1px 1px #808080,
        inset -2px -2px #d4d0c8,
        inset 2px 2px #404040;
      scrollbar-width: auto;
      scrollbar-color: auto;
    }
    .list::-webkit-scrollbar {
      width: 16px;
      height: 16px;
    }
    .list::-webkit-scrollbar-track {
      background-color: #fff;
      background-image: conic-gradient(
        #d4d0c8 25%,
        #fff 0 50%,
        #d4d0c8 0 75%,
        #fff 0
      );
      background-size: 2px 2px;
    }
    .list::-webkit-scrollbar-thumb {
      background: #d4d0c8;
      border-radius: 0;
      box-shadow:
        inset -1px -1px #404040,
        inset 1px 1px #fff,
        inset -2px -2px #808080,
        inset 2px 2px #dfdfdf;
      min-height: 24px;
    }
    .list::-webkit-scrollbar-button {
      display: none;
    }
    .list::-webkit-scrollbar-button:vertical:single-button {
      display: block;
      height: 16px;
      background-color: #d4d0c8;
      background-position: center;
      background-repeat: no-repeat;
      box-shadow:
        inset -1px -1px #404040,
        inset 1px 1px #fff,
        inset -2px -2px #808080,
        inset 2px 2px #dfdfdf;
    }
    .list::-webkit-scrollbar-button:vertical:decrement {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='4' viewBox='0 0 7 4'%3E%3Cpath d='M3 0h1v1H2v1H1v1H0v1h7V3H6V2H5V1H4V0Z'/%3E%3C/svg%3E");
    }
    .list::-webkit-scrollbar-button:vertical:increment {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='4' viewBox='0 0 7 4'%3E%3Cpath d='M0 0h7v1H6v1H5v1H4v1H3V3H2V2H1V1H0Z'/%3E%3C/svg%3E");
    }
    .list::-webkit-scrollbar-button:active {
      box-shadow: inset 1px 1px #808080;
    }
    .option {
      display: flex;
      align-items: center;
      gap: 5px;
      min-height: 24px;
      padding: 0 4px;
      cursor: default;
      line-height: 24px;
    }
    .option input {
      appearance: none;
      -webkit-appearance: none;
      flex: 0 0 13px;
      width: 13px;
      height: 13px;
      margin: 0;
      border: 1px solid #808080;
      border-radius: 0;
      background: #fff;
      position: relative;
    }
    .option input:checked::after {
      content: "";
      position: absolute;
      inset: 2px 1px;
      background: #000;
      clip-path: polygon(0 3px, 2px 5px, 7px 0, 7px 3px, 2px 8px, 0 6px);
    }
    .option input:focus {
      outline: none;
    }
    .option:focus-within .text {
      background: #0a246a;
      color: #fff;
      outline: 1px dotted #fff;
      outline-offset: -1px;
    }
    .option input:disabled {
      background: #d4d0c8;
    }
    .option input:disabled::after {
      background: #808080;
    }
    fieldset:disabled .option {
      color: #808080;
    }
    .text {
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      padding: 0 2px;
    }
    code {
      font: inherit;
    }
    .description {
      color: #666;
      margin-left: 8px;
    }
    .option:focus-within .description {
      color: inherit;
    }
    .empty {
      margin: 8px 4px;
      color: #666;
    }
    @media (forced-colors: active) {
      .option input {
        appearance: auto;
      }
      .option input::after {
        display: none;
      }
      button,
      .filter input,
      .list {
        border: 1px solid ButtonText;
      }
      .option:focus-within .text {
        background: Highlight;
        color: HighlightText;
      }
    }
  `;

  private languageChanged = () => this.requestUpdate();
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("languagechange", this.languageChanged);
  }
  disconnectedCallback() {
    window.removeEventListener("languagechange", this.languageChanged);
    super.disconnectedCallback();
  }

  private selectAll() {
    this.emitSelection([
      ...new Set([
        ...this.selected,
        ...this.filteredSkills().map((skill) => skill.name),
      ]),
    ]);
  }

  private deselectAll() {
    const visible = new Set(this.filteredSkills().map((skill) => skill.name));
    this.emitSelection(this.selected.filter((name) => !visible.has(name)));
  }

  private emitSelection(next: string[]) {
    if (this.disabled) return;
    this.selected = next;
    this.dispatchEvent(
      new CustomEvent<string[]>("selection-change", {
        detail: [...next],
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** 读取当前选中的技能名称，便于表单提交。 */
  readValue(): string[] {
    return [...this.selected];
  }

  private toggle(name: string, checked: boolean) {
    const next = checked
      ? [...new Set([...this.selected, name])]
      : this.selected.filter((value) => value !== name);
    this.emitSelection(next);
  }

  private description(skill: A2kSkillOption) {
    return i18n.language === "zh-CN"
      ? skill.description_zh || skill.description
      : skill.description || skill.description_zh;
  }

  private filteredSkills() {
    const query = this.filter.trim().toLocaleLowerCase();
    return this.skills.filter(
      (skill) =>
        !query ||
        `${skill.name} ${this.description(skill) || ""}`
          .toLocaleLowerCase()
          .includes(query),
    );
  }

  render() {
    const visible = this.filteredSkills();
    return html`<fieldset ?disabled=${this.disabled}>
      <legend>${i18n.t("skillMultiSelectTitle")}</legend>
      ${
        this.disabled
          ? nothing
          : html`<div class="filter">
                <input
                  type="text"
                  aria-label=${i18n.t("skillMultiSelectFilter")}
                  placeholder=${i18n.t("skillMultiSelectFilter")}
                  .value=${this.filter}
                  @input=${(event: Event) => {
                    this.filter = (event.target as HTMLInputElement).value;
                  }}
                />
              </div>
              <div class="toolbar">
                <button
                  type="button"
                  ?disabled=${!visible.length}
                  @click=${(event: Event) => {
                    event.preventDefault();
                    this.selectAll();
                  }}
                >
                  ${i18n.t(this.filter ? "selectVisible" : "selectAll")}
                </button>
                <button
                  type="button"
                  ?disabled=${!visible.length}
                  @click=${(event: Event) => {
                    event.preventDefault();
                    this.deselectAll();
                  }}
                >
                  ${i18n.t(this.filter ? "deselectVisible" : "deselectAll")}
                </button>
              </div>`
      }
      <div
        class="list"
        role="group"
        aria-label=${i18n.t("skillMultiSelectTitle")}
      >
        ${
          visible.length
            ? visible.map((skill) => {
                const isSelected = this.selected.includes(skill.name);
                return html`<label
                  class="option"
                  title=${`${skill.name}: ${this.description(skill) || ""}`}
                  data-selected=${String(isSelected)}
                >
                  <input
                    type="checkbox"
                    .checked=${isSelected}
                    @change=${(event: Event) => this.toggle(skill.name, (event.target as HTMLInputElement).checked)}
                  />
                  <span class="text"
                    ><code>${skill.name}</code>${
                      this.description(skill)
                        ? html` <span class="description">
                            ${this.description(skill)}
                          </span>`
                        : nothing
                    }</span
                  >
                </label>`;
              })
            : html`<p class="empty">${i18n.t("skillMultiSelectEmpty")}</p>`
        }
      </div>
    </fieldset>`;
  }
}
